import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    // Create socket connection with enhanced options
    const newSocket = io(`${process.env.REACT_APP_URL}`, {
      // Reconnection options for persistence
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: false,
      
      // Allow fallback transports - this is crucial!
      transports: ['websocket', 'polling'],
      
      // Additional options for better reliability
      upgrade: true,
      rememberUpgrade: true,
      
      // CORS and security
      withCredentials: false,
      
      // Query parameters (if needed for authentication)
      // query: {
      //   token: 'your-auth-token'
      // }
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connection event handlers
    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);
      setConnectionError(null);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      
      // Handle different disconnect reasons
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, reconnect manually
        newSocket.connect();
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionError(null);
    });

    newSocket.on('reconnect_error', (error) => {
      console.log('Socket reconnection failed:', error);
      setConnectionError(error.message);
    });

    newSocket.on('connect_error', (error) => {
      console.log('Socket connection error:', error);
      setConnectionError(error.message);
      
      // If websocket fails, force polling transport
      if (error.message.includes('websocket')) {
        console.log('WebSocket failed, falling back to polling');
        newSocket.io.opts.transports = ['polling'];
      }
    });

    // Handle reconnect attempts
    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Reconnection attempt:', attemptNumber);
    });

    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }
    };
  }, []); // Empty dependency array - runs only once

  // Provide socket instance and connection status
  const value = {
    socket,
    isConnected,
    connectionError,
    
    // Helper method to emit events with connection check
    emit: (event, data) => {
      if (socket && isConnected) {
        socket.emit(event, data);
        return true;
      } else {
        console.warn('Cannot emit - socket not connected');
        return false;
      }
    },
    
    // Helper method to listen to events
    on: (event, callback) => {
      if (socket) {
        socket.on(event, callback);
        // Return cleanup function
        return () => socket.off(event, callback);
      }
    },
    
    // Helper method to join rooms
    joinRoom: (room) => {
      if (socket && isConnected) {
        socket.emit('joinRoom', { room });
      }
    },
    
    // Manual reconnect method
    reconnect: () => {
      if (socket) {
        socket.connect();
      }
    }
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};